import { action, observable, runInAction } from "mobx";

import { getCurrentCulture } from "@utils";
import { StorageStateKeysEnum } from "@utils/storage";
import KeepAliveApiService from "@api-services/platform/KeepAliveApiService";
import type { RootStore } from "@state/stores/types";
import { history } from "@state";
import BasePopupStore from "@state/stores/popups/BasePopupStore";
import { redirectToLogin } from "@utils/auth";
import { createApiRequestOptions } from "@utils/api-config/createApiRequestOptions";
import { RequestContextType } from "@utils/api-config/RequestContext";

class SessionExpireStore extends BasePopupStore {
	@observable isGameOpen: boolean = false;

	constructor(rootStore: RootStore) {
		super(rootStore);
		window.addEventListener("storage", (e) => {
			if (e.key === "session-expiration-time") {
				// @ts-expect-error
				this.startTimer(DefaultSessionDuration, true);
			}
		});

		document.addEventListener("click", this.onClick);
	}

	slideToken() {
		KeepAliveApiService.ping(
			createApiRequestOptions(RequestContextType.Default)
		);

		if (this.rootStore.userAuthStore.secondaryUser) {
			const secondaryContext = this.rootStore.userAuthStore.secondaryUser
				.isInternetUser
				? RequestContextType.Online
				: RequestContextType.Shop;
			KeepAliveApiService.ping(createApiRequestOptions(secondaryContext));
		}
	}

	addSlideTokenInterval() {
		this.slideToken();
		const pingInterval = setInterval(() => {
			this.slideToken();
		}, 600000);

		if (!this.isGameOpen) {
			clearInterval(pingInterval);
		}
	}

	shouldStartTimer() {
		return (
			this.rootStore.userAuthStore.user != null &&
			// @ts-expect-error
			DefaultSessionDuration != null &&
			// @ts-expect-error
			!isNaN(DefaultSessionDuration) &&
			!this.rootStore.offerHub.isSessionExpired &&
			!this.rootStore.realityCheckStore.isActive &&
			!this.rootStore.automaticLogoutStore.isActive &&
			!this.rootStore.isDepositLimitUpdatePopupActive &&
			!this.rootStore.sessionExpireStore.isGameOpen
		);
	}

	onInitialize() {
		this.isGameOpen =
			this.rootStore.localStorageProvider.get(
				StorageStateKeysEnum.GAME_STATE
			) === "true";
		if (this.shouldStartTimer()) {
			// @ts-expect-error
			this.startTimer(DefaultSessionDuration);
		}
	}

	@action.bound
	startTimer(expiresIn: number, receivedMessage = false) {
		this.clearTimer();
		const currentDate = new Date();

		const sessionExpirationTime = currentDate.getTime() + expiresIn * 1000;
		if (
			this.rootStore.localStorageProvider.get(
				StorageStateKeysEnum.SESSION_EXPIRATION_TIME
			) == null ||
			!receivedMessage
		) {
			this.rootStore.localStorageProvider.set(
				StorageStateKeysEnum.SESSION_EXPIRATION_TIME,
				sessionExpirationTime
			);
		}

		this.timerId = setTimeout(() => {
			runInAction(async () => {
				if (
					this.rootStore.userAuthStore.isLoggedIn &&
					this.rootStore.localStorageProvider.get(
						StorageStateKeysEnum.SLN
					) !== "true" &&
					!this.rootStore.automaticLogoutStore.isActive
				) {
					if (this.isGameOpen) {
						return;
					}

					const newCurrentDate = new Date();
					const localStorageSessionExpiryTime =
						this.rootStore.localStorageProvider.get(
							StorageStateKeysEnum.SESSION_EXPIRATION_TIME
						) as number;

					if (
						newCurrentDate.getTime() <=
						localStorageSessionExpiryTime
					) {
						return;
					}

					// redirect after session expire, don't show popup
					// logout user

					await this.rootStore.userAuthStore.logoutUser({
						isTimeoutLogout: true,
					});

					if (!this.rootStore.automaticLogoutStore.isActive) {
						this.rootStore.closePopups();
						this.rootStore.resetPopups();
						redirectToLogin("isSessionExpired=true");
					}
				}
			});
		}, expiresIn * 1000);
	}

	onClick = () => {
		const isLiveOfferPathnameActive = history.location.pathname.includes(
			`/${getCurrentCulture()}/live`
		);

		if (
			this.rootStore.userAuthStore.user != null &&
			!isLiveOfferPathnameActive &&
			!this.rootStore.offerHub.isSessionExpired &&
			!this.rootStore.realityCheckStore.isActive &&
			!this.rootStore.automaticLogoutStore.isActive &&
			!this.rootStore.sessionExpireStore.isGameOpen
		) {
			// @ts-expect-error
			this.startTimer(DefaultSessionDuration);
		}
	};

	setIsGameOpened(state: boolean): void {
		this.isGameOpen = state;
		this.rootStore.localStorageProvider.set(
			StorageStateKeysEnum.GAME_STATE,
			state ? "true" : "false"
		);
	}
}

export default SessionExpireStore;
